(() => {
  const normalize = (value) => String(value).replace(/[“”″]/g, '"').replace(/’/g, "'");
  const shape = (text) => {
    if (typeof text !== 'string') return null;
    const first = text.search(/\d/);
    if (first === -1) return null;
    let last = text.length - 1;
    while (last >= 0 && !/\d/.test(text[last])) last -= 1;
    return { prefix: text.slice(0, first), body: text.slice(first, last + 1), suffix: text.slice(last + 1) };
  };
  const deriveMoneyFormat = (unitProbe, groupedProbe) => {
    const unit = shape(unitProbe);
    const grouped = shape(groupedProbe);
    if (!unit || !grouped) return null;
    const unitSeps = unit.body.match(/\D/g) || [];
    if (unitSeps.length > 1) return null;
    const decimalSep = unitSeps[0] || '';
    const intPart = decimalSep ? unit.body.split(decimalSep)[0] : unit.body;
    const decPart = decimalSep ? unit.body.split(decimalSep)[1] || '' : '';
    const decimals = decPart.length;
    if (decimals > 3) return null;
    const rendered = Number(`${intPart}.${decPart || '0'}`);
    if (!rendered) return null;
    const divisor = Math.round(100 / rendered);
    if (divisor !== 1 && divisor !== 100 && divisor !== 1000) return null;
    let groupedBody = grouped.body;
    if (decimals > 0) groupedBody = groupedBody.slice(0, -(decimals + decimalSep.length));
    const thousandsSep = (groupedBody.match(/\D/g) || [])[0] || '';
    return { prefix: unit.prefix, suffix: unit.suffix, decimals, decimalSep, thousandsSep, divisor };
  };
  const formatMoney = (minorUnits, fmt) => {
    if (!fmt) {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(minorUnits / 100);
    }
    const value = minorUnits / fmt.divisor;
    const sign = value < 0 ? '-' : '';
    const parts = Math.abs(value).toFixed(fmt.decimals).split('.');
    const grouped = fmt.thousandsSep ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, fmt.thousandsSep) : parts[0];
    const body = parts[1] ? grouped + fmt.decimalSep + parts[1] : grouped;
    return sign + fmt.prefix + body + fmt.suffix;
  };
  const money = (cents, unit, grouped) => formatMoney(cents, deriveMoneyFormat(unit, grouped));

  if (!customElements.get('cq-product-options')) customElements.define('cq-product-options', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.variants = JSON.parse(this.querySelector('[data-option-variants]').textContent);
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.groups = [...this.querySelectorAll('[data-option-index]')];
      this.addEventListener('change', event => {
        if (event.target.matches('[name="quantity"]')) {
          this.clampQuantity();
          this.renderTotal();
        } else this.update({ clampQuantity: true });
      });
      this.addEventListener('input', event => {
        if (event.target.matches('[name="quantity"]') && event.target.checkValidity()) this.renderTotal();
      });
      this.addEventListener('submit', (event) => {
        const plan = this.querySelector('[name="selling_plan"]:checked')?.value;
        if (!this.variant?.available || ((plan || this.variant?.requiresPlan || this.dataset.requiresPlan === 'true') && !this.variant.plans.some(p => String(p.id) === plan))) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.querySelector('product-form')?.handleErrorMessage(this.dataset.unavailableLabel);
        }
      }, true);
      this.update();
    }

    disconnectedCallback() { clearTimeout(this.totalAnnouncement); }

    update({ clampQuantity = false } = {}) {
      const selected = this.groups.map(group => group.querySelector('input:checked')?.value);
      const matches = this.variants.filter(v => selected.every((value, i) => normalize(v.options[i]) === value));
      this.variant = matches.find(v => v.available) || matches[0];
      const variant = this.variant;
      this.groups.forEach((group, index) => {
        group.querySelectorAll('input').forEach(input => {
          // Earlier options constrain later ones, so all valid combinations remain reachable.
          const available = this.variants.some(v => v.available && normalize(v.options[index]) === input.value && selected.slice(0, index).every((value, i) => normalize(v.options[i]) === value));
          input.closest('label').classList.toggle('is-unavailable', !available);
          input.setAttribute('aria-description', available ? '' : this.dataset.soldLabel);
        });
      });
      const planInputs = [...this.querySelectorAll('[name="selling_plan"]')];
      planInputs.forEach(input => {
        const available = input.value ? variant?.plans.some(p => String(p.id) === input.value) : !(variant?.requiresPlan || this.dataset.requiresPlan === 'true');
        input.disabled = !available;
        input.closest('label').classList.toggle('is-unavailable', !available);
        const price = input.value ? variant?.plans.find(plan => String(plan.id) === input.value) : variant;
        const label = input.closest('label').querySelector('[data-option-plan-price]');
        if (label) label.textContent = price?.priceText || this.dataset.unavailableLabel;
      });
      if (!planInputs.some(input => input.checked && !input.disabled)) {
        planInputs.forEach(input => { input.checked = false; });
        const first = planInputs.find(input => !input.disabled);
        if (first) first.checked = true;
      }
      const planId = planInputs.find(input => input.checked)?.value;
      const choicePrice = item => {
        const current = planId && item.plans.find(plan => String(plan.id) === planId);
        if (current) return current;
        if (!item.requiresPlan && this.dataset.requiresPlan !== 'true') return item;
        // Match the plan the form will select when this variant requires a different subscription.
        const first = planInputs.find(input => input.value && item.plans.some(plan => String(plan.id) === input.value));
        return first && item.plans.find(plan => String(plan.id) === first.value);
      };
      this.groups.forEach((group, index) => {
        group.querySelectorAll('input').forEach(input => {
          const candidates = this.variants.filter(item => normalize(item.options[index]) === input.value
            && selected.slice(0, index).every((value, i) => normalize(item.options[i]) === value));
          const exact = candidates.find(item => selected.every((value, i) => i === index || normalize(item.options[i]) === value));
          const prices = (exact ? [exact] : candidates).map(choicePrice).filter(Boolean);
          const lowest = prices.reduce((low, price) => !low || price.price < low.price ? price : low, null);
          const text = !lowest ? this.dataset.unavailableLabel : prices.some(price => price.price !== lowest.price)
            ? this.dataset.fromLabel.replace('[price]', lowest.priceText) : lowest.priceText;
          const label = input.closest('label').querySelector('[data-option-choice-price]');
          if (label) label.textContent = text;
        });
      });
      const price = variant?.plans.find(p => String(p.id) === planId) || variant;
      const validPlan = (!variant?.requiresPlan && this.dataset.requiresPlan !== 'true') || variant?.plans.some(p => String(p.id) === planId);
      const submit = this.form.querySelector('[type="submit"]');
      submit.disabled = !variant?.available || !validPlan;
      this.querySelector('[data-option-submit-label]').textContent = !variant ? this.dataset.unavailableLabel : !variant.available ? this.dataset.soldLabel : this.dataset.addLabel;
      this.form.querySelector('[name="id"]').value = variant?.id || '';
      this.querySelector('[data-option-price]').textContent = price?.priceText || this.dataset.unavailableLabel;
      const compare = this.querySelector('[data-option-compare]');
      compare.hidden = !price || price.compare <= price.price;
      compare.textContent = price?.compareText || '';
      const quantity = this.form.querySelector('[name="quantity"]');
      const rule = variant?.quantity || {};
      quantity.min = rule.min || 1;
      quantity.step = rule.increment || 1;
      if (rule.max) quantity.max = rule.max; else quantity.removeAttribute('max');
      if (clampQuantity) this.clampQuantity();
      this.renderTotal();
      this.dispatchEvent(new CustomEvent('cq:variant-change', { bubbles: true, detail: { variant, totalText: this.totalText } }));
    }

    clampQuantity() {
      const quantity = this.form.querySelector('[name="quantity"]');
      if (Number(quantity.value) < Number(quantity.min)) quantity.value = quantity.min;
      if (quantity.max && Number(quantity.value) > Number(quantity.max)) quantity.value = quantity.max;
    }

    renderTotal() {
      const variant = this.variant;
      const planId = this.querySelector('[name="selling_plan"]:checked')?.value;
      const price = variant?.plans.find(plan => String(plan.id) === planId) || variant;
      const validPlan = (!variant?.requiresPlan && this.dataset.requiresPlan !== 'true') || variant?.plans.some(plan => String(plan.id) === planId);
      const quantity = this.form.querySelector('[name="quantity"]');
      const count = Number(quantity.value || quantity.min);
      const totalText = price && validPlan && quantity.checkValidity() && Number.isFinite(count) && count > 0 ? money(price.price * count, this.dataset.moneyUnit, this.dataset.moneyGrouped) : this.dataset.unavailableLabel;
      const total = this.querySelector('[data-option-total]');
      if (total) total.textContent = totalText;
      if (this.totalText !== totalText) {
        this.totalText = totalText;
        clearTimeout(this.totalAnnouncement);
        this.totalAnnouncement = setTimeout(() => {
          const status = this.querySelector('[data-option-total-status]');
          if (status) status.textContent = this.dataset.totalStatus.replace('[total]', totalText);
        }, 180);
      }
      this.dispatchEvent(new CustomEvent('cq:purchase-total', { bubbles: true, detail: { totalText } }));
    }
  });

  // Legacy controls above remain for the custom-bundle subclass (snippets/quick-view-options.liquid).
  // Every catalog Options / Quick View opener below uses the r3-pdp2 configure dialog.
  // The dialog's CSS/JS URLs come from the first Section Rendering response (Liquid asset_url),
  // so each file keeps its own version key and a stale copy is never reused.
  const productUrlFor = (opener) => {
    const card = opener.closest('.card-product, .cq-product-card, .product-row, .product-compare-product');
    return opener.dataset.productUrl || card?.querySelector('.js-product-link')?.getAttribute('href') || null;
  };
  const sectionUrl = (productUrl) => {
    const url = new URL(productUrl, location.origin);
    url.searchParams.set('section_id', 'cq-configure');
    return url.href;
  };
  const responses = new Map();
  const fetchSection = (productUrl) => {
    const key = sectionUrl(productUrl);
    if (!responses.has(key)) {
      responses.set(key, fetch(key, { cache: 'no-cache' }).then((response) => {
        if (!response.ok) throw new Error('Configure section unavailable');
        return response.text();
      }).catch((error) => { responses.delete(key); throw error; }));
    }
    return responses.get(key);
  };
  const loadAsset = (url, style = false) => new Promise((resolve, reject) => {
    if (!url) { reject(new Error('Configure asset missing')); return; }
    const existing = [...document.querySelectorAll(style ? 'link[rel="stylesheet"]' : 'script[src]')]
      .find((element) => element.getAttribute(style ? 'href' : 'src') === url);
    if (existing && (!style || existing.sheet)) { resolve(); return; }
    const element = existing || document.createElement(style ? 'link' : 'script');
    element.addEventListener('load', resolve, { once: true });
    element.addEventListener('error', () => { element.remove(); reject(new Error('Configure asset unavailable')); }, { once: true });
    if (existing) return;
    if (style) { element.rel = 'stylesheet'; element.href = url; }
    else { element.src = url; element.async = false; }
    document.head.append(element);
  });
  let ready = null;
  const ensure = (html) => {
    if (customElements.get('cq-option-picker')) return Promise.resolve();
    if (!ready) {
      const assets = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-configure-product]')?.dataset || {};
      ready = Promise.all([
        loadAsset(assets.pickerCss, true),
        loadAsset(assets.configureCss, true),
        customElements.get('cq-purchase-options') ? null : loadAsset(assets.pickerJs),
      ])
        .then(() => (customElements.get('cq-option-picker') ? null : loadAsset(assets.configureJs)))
        .then(() => customElements.whenDefined('cq-option-picker'))
        .catch((error) => { ready = null; throw error; });
    }
    return ready;
  };
  // r8 item 31 (PDP spec section 5, open point 1): the Configure popup has no Bold Product Options mount, so a
  // product whose add-ons include a required group ("Add Specialty Filter?*", "Connection Type*") could be added from
  // it with no add-on choice. Such a product's Options / Configure / Quick view opener goes to the full product
  // page instead (same button, same label), where Bold asks for the choice. Bold's own option data decides: the
  // generate_option answer its script loads on the product page (the browser keeps it for 4 hours), checked once
  // per product, started when the shopper points at or focuses the opener. When the answer cannot be had in time
  // (error, timeout, no product id), the opener goes to the product page too: it always works.
  const BOLD_WAIT = 2500;
  const boldChecks = new Map();
  const productHandle = (productUrl) => new URL(productUrl, location.origin).pathname.split('/products/')[1]?.split('/')[0] || '';
  // The card's own product id, or the product form's (a card's direct Add to cart); other openers (list rows, compare
  // columns, the cart offer) resolve it from the handle.
  const productIdFor = (opener) => opener.dataset.productId || opener.closest('product-card, .cq-product-card')?.dataset.productId
    || opener.form?.querySelector('[name="product-id"]')?.value || '';
  const resolveId = (opener, productUrl) => {
    const id = productIdFor(opener);
    if (id) return Promise.resolve(id);
    const handle = productHandle(productUrl);
    if (!handle) return Promise.resolve('');
    return fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => String(product?.id || ''))
      .catch(() => '');
  };
  // Resolves 'required' (a required group), 'optional' (add-ons, none of them required), 'none' (no add-ons) or
  // 'unknown' (no answer). A definite answer is also kept in boldAnswers, so a submit can be let through (or not) at
  // once. r10 item 7 (AJ 2026-10-09: "some products like the undersink fluoride configure doesnt pop up and takes them to
  // product page wonder why?"): only a group Bold marks required sends a product to its page; checked 2026-10-09
  // against Bold's data for all 527 products, 11 have one (the under sink systems' "Connection Type" and the high flow
  // filter's "Add Specialty Filter?"), 94 have optional add-ons only and open the popup with a note that more add-ons
  // are on the full page.
  // Lane B (AJ 2026-10-10, Thunder RO cards: "we need that default one to come through"): a paid value Bold preselects
  // is 'required' too. Bold adds it with the product on the product page only (its own form and cart properties; it
  // documents no way to add it from another form), so a card, compare or cart Add that skipped it put a cheaper item in
  // the cart than the page shows. Checked 2026-10-10 against Bold's data for all 527 products: the 16 Thunder systems
  // (1000C to 4000MP) preselect "Faucets Style" Chrome at $29.95; none has a required group. A preselected value that
  // costs nothing ("No Remineralizer", "1.5 cubic feet") changes nothing, so those products keep their card Add.
  const paidDefault = (option) => option.type !== 'displaytext'
    && (option.options_values || []).some((value) => value.selected === true && Number(value.price) > 0);
  const boldAnswers = new Map();
  const boldKey = (opener, productUrl) => productIdFor(opener) || productHandle(productUrl);
  const boldRequired = (opener, productUrl) => {
    const key = boldKey(opener, productUrl);
    if (!key) return Promise.resolve('unknown');
    if (!boldChecks.has(key)) {
      const check = resolveId(opener, productUrl).then((id) => {
        const shop = window.Shopify?.shop;
        if (!id || !shop) throw new Error('No product id');
        return fetch(`https://options.shopapps.site/v2/${shop}/generate_option/${id}`, { credentials: 'omit' });
      }).then((response) => {
        if (!response.ok) throw new Error('No Bold answer');
        return response.json();
      }).then((data) => {
        const options = (data?.option_product?.option_sets || []).flatMap((set) => set.options || []);
        let answer = 'none';
        if (options.some((option) => option.required === true || paidDefault(option))) answer = 'required';
        else if (options.some((option) => option.type !== 'displaytext')) answer = 'optional';
        boldAnswers.set(key, answer);
        return answer;
      })
        .catch(() => { boldChecks.delete(key); return 'unknown'; });
      boldChecks.set(key, check);
    }
    return boldChecks.get(key);
  };
  // No required group: the popup or the card's own Add works.
  const direct = (answer) => answer === 'none' || answer === 'optional';
  // Once known, an opener that leads to the product page stops announcing a dialog. r10 item 7: its label says where it
  // goes ("Configure" or "Quick view" becomes "Choose options", data-cq-page-label), and on a product card it becomes
  // the card's one action: the card's Add to cart (which goes to the same page) is hidden and the opener takes the
  // full row in the solid style (component-card.css).
  // Lane B: a quick Add with no opener beside it (the compare column, the collection's compact rows, a cart suggestion,
  // the search result) carries the same data-cq-page-label (and data-cq-page-aria where it has an aria-label), so it
  // reads "Choose options" (the cart's "Choose") and still goes to the page through the submit rule below.
  const markFullPage = (opener) => {
    opener.setAttribute('data-cq-full-page', '');
    ['aria-haspopup', 'aria-controls', 'aria-expanded'].forEach((name) => opener.removeAttribute(name));
    const label = opener.querySelector('[data-cq-opener-label]');
    const text = opener.dataset.cqPageLabel;
    if (label && text && label.textContent !== text) label.textContent = text;
    if (opener.dataset.cqPageAria) opener.setAttribute('aria-label', opener.dataset.cqPageAria);
    const actions = opener.closest('.cq-card-actions');
    if (actions && opener.classList.contains('cq-card-btn-line')) {
      // The card's Add hides; keyboard focus on it moves to this button, which takes its place.
      const add = actions.querySelector('.button-add-to-cart');
      const moveFocus = Boolean(add) && add === document.activeElement;
      actions.setAttribute('data-cq-full-page', '');
      opener.classList.replace('cq-card-btn-line', 'cq-card-btn-solid');
      if (moveFocus) opener.focus({ preventScroll: true });
    }
  };
  // Every button in a scope (a card's actions, a compare column, a cart suggestion) whose label or target changes when
  // the product must open its page.
  const PAGE_BUTTONS = '[data-cq-choose-options], .button-quick-view, form.no_bold_options [type="submit"][data-cq-page-label]';
  const markScope = (scope) => {
    [scope, ...scope.querySelectorAll(PAGE_BUTTONS)].filter((element) => element.matches(PAGE_BUTTONS)).forEach(markFullPage);
  };
  const needsProductPage = (opener, productUrl) => Promise.race([
    boldRequired(opener, productUrl),
    new Promise((resolve) => { setTimeout(() => resolve('unknown'), BOLD_WAIT); }),
  ]).then((answer) => !direct(answer));

  const open = async (opener) => {
    if (!opener) return;
    const productUrl = productUrlFor(opener);
    if (!productUrl) return;
    if (opener.hasAttribute('data-cq-full-page')) { location.assign(productUrl); return; }
    opener.setAttribute('aria-busy', 'true');
    try {
      // The first popup's code and options load while Bold answers.
      const section = customElements.get('cq-option-picker') ? null : fetchSection(productUrl);
      section?.catch(() => {});
      if (await needsProductPage(opener, productUrl)) {
        markFullPage(opener);
        location.assign(productUrl);
        return;
      }
      // r10 item 7: the popup shows its "More add-ons on the full page" note for a product with optional add-ons.
      opener.dataset.cqBold = boldAnswers.get(boldKey(opener, productUrl)) || '';
      let html = null;
      if (section) {
        html = await section;
        await ensure(html);
      }
      document.querySelector('cq-option-picker')?.open(opener, html);
    } catch (_) {
      location.assign(productUrl);
    } finally {
      opener.removeAttribute('aria-busy');
    }
  };
  // r8 item 31 fix: a card's own Add to cart (the no_bold_options product form, card-product.liquid and
  // cq-card-actions.liquid) skipped Bold too, so a required group ("Connection Type*") could still be left out from
  // a card. The same rule applies: when Bold's data shows a required group, or gives no answer in time, the button
  // goes to the product page (same label), where Bold asks for the choice; with no required group the add goes on
  // to product-form.js as before. The check starts when the shopper points at, taps or focuses the button (below),
  // so a known answer lets the add through at once; otherwise the button shows its spinner while Bold answers.
  const cardAddUrl = (form, button) => productUrlFor(button)
    || (form.querySelector('[name="id"]')?.value ? `${window.Shopify?.routes?.root || '/'}variants/${form.querySelector('[name="id"]').value}` : null);
  const busy = (button, on) => {
    button.toggleAttribute('aria-busy', on);
    button.classList.toggle('loading', on);
    button.querySelector('.button-overlay-spinner')?.classList.toggle('hidden', !on);
  };
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('no_bold_options') || event.defaultPrevented) return;
    const button = (event.submitter?.form === form && event.submitter) || form.querySelector('[type="submit"]');
    if (!button) return;
    const productUrl = cardAddUrl(form, button);
    if (!productUrl) return;
    const key = boldKey(button, productUrl);
    if (key && direct(boldAnswers.get(key))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (form.hasAttribute('data-cq-bold-wait')) return;
    if (key && boldAnswers.get(key) === 'required') { busy(button, true); location.assign(productUrl); return; }
    form.setAttribute('data-cq-bold-wait', '');
    busy(button, true);
    needsProductPage(button, productUrl).then((page) => {
      if (page) { location.assign(productUrl); return; }
      form.removeAttribute('data-cq-bold-wait');
      busy(button, false);
      if (form.requestSubmit) form.requestSubmit(button); else button.click();
    });
  }, true);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cq-choose-options]');
    if (!opener) return;
    event.preventDefault();
    open(opener);
  });
  document.addEventListener('cq:configure-open', (event) => open(event.detail?.opener));
  // Warm once per page: the first opener the shopper points at or tabs to loads the dialog
  // code and that product's options, so the first click opens without waiting.
  let warmed = false;
  const warm = (event) => {
    // r8 item 31 fix: a card's direct Add to cart starts its product's Bold check too (no popup to warm), from the
    // card's actions panel or the phone "+" that opens it, so the answer is usually in before the tap or click.
    const scope = event.target.closest?.('.cq-card-panel, .cq-card-plus, .cq-compare-buy');
    const add = event.target.closest?.('form.no_bold_options [type="submit"]')
      || (scope && (scope.closest('.cq-card') || scope).querySelector('form.no_bold_options [type="submit"]'));
    if (add && !add.hasAttribute('data-cq-bold-checked')) {
      const addUrl = cardAddUrl(add.form, add);
      if (addUrl) {
        add.setAttribute('data-cq-bold-checked', '');
        // A required answer also stops the card's Configure / Quick view opener announcing a dialog, and relabels an
        // Add that stands alone (Lane B).
        boldRequired(add, addUrl).then((answer) => {
          if (answer === 'required') markScope(add.closest('.cq-card-actions, .cq-cmp-action') || add);
        });
      }
    }
    // Lane F, kept by lane H: pointing at a card's actions or tapping its phone "+" also warms the card's Configure /
    // Quick view opener (or its one "Choose options"), so the popup opens without waiting.
    const opener = event.target.closest?.('[data-cq-choose-options], .button-quick-view')
      || (scope ? (scope.closest('.cq-card') || scope).querySelector('[data-cq-choose-options], .button-quick-view') : null);
    const productUrl = opener && productUrlFor(opener);
    if (!productUrl) return;
    // r8 item 31: every opener the shopper points at or tabs to starts its product's Bold check (once per product).
    if (!opener.hasAttribute('data-cq-bold-checked')) {
      opener.setAttribute('data-cq-bold-checked', '');
      boldRequired(opener, productUrl).then((answer) => { if (answer === 'required') markFullPage(opener); });
    }
    if (warmed || customElements.get('cq-option-picker')) return;
    warmed = true;
    fetchSection(productUrl).then(ensure).catch(() => { warmed = false; });
  };
  document.addEventListener('pointerover', warm, { passive: true });
  document.addEventListener('focusin', warm);

  // r10 item 7: a product card's actions are checked against Bold when the card comes near the screen (not only when
  // pointed at), so a card whose product must open its page already reads "Choose options" when its actions show. Bold's
  // answer is cached by its CDN and the browser for 4 hours; each product is checked once per page.
  // Lane F2 (slow connections, about 1.6 Mbps and 150 ms): at most 6 checks run at once (each answer is about 2 KB from
  // Bold's CDN), and the queue is ordered, not first come: held actions on screen first, then held actions just below
  // it, then actions whose label is already final (a "Choose options" whose check only decides popup or page). A hidden
  // copy is never checked until it shows: the collection's compact rows (#product-rows, rendered hidden beside the grid
  // for the layout switch) wait for the screen like the cards do, instead of being checked first and holding up the
  // visible cards behind 16 hidden answers.
  // Lane B (no flicker): the actions whose labels hang on that answer are rendered held, data-cq-bold-pending on the card
  // panel (card-product.liquid), the compare column's action (product-compare-column.liquid) and the compact row's Add
  // (cq-card-actions.liquid). As rendered, base.css hides a held element for 15 s at most (in case this script never
  // runs; lane F2, was 4 s, which ran out before this deferred script ran on a slow connection); once taken here it
  // stays hidden ("wait" until it comes near the screen, then "check") until the product's
  // answer is in. The hold is lifted after the page marks are applied, in the same task, so the buttons first show with
  // their final labels; with no answer by BOLD_WAIT it is lifted as rendered (the button then goes to the product page,
  // as before). A product already answered on this page (a compare column, a re-rendered card) is settled as it
  // arrives, before it paints. A cart suggestion's or search result's quick Add is never hidden from focus (the cart
  // moves keyboard focus straight to the next suggestion's Add): it is checked as soon as it appears, and one that
  // arrives after the page has loaded (the cart's suggestion rows, a search result) with no answer yet is only faded out
  // ("soft") until the answer is in, so it can still take focus.
  const HOLD = 'data-cq-bold-pending';
  const UNITS = `.cq-card-panel, .cq-cmp-action, [${HOLD}], form.no_bold_options [type="submit"][data-cq-page-label]`;
  const unitTarget = (unit) => {
    const opener = unit.querySelector('[data-cq-choose-options], .button-quick-view');
    const add = unit.matches('form.no_bold_options [type="submit"]') ? unit : unit.querySelector('form.no_bold_options [type="submit"]');
    const subject = opener || add;
    return { subject, add, url: opener ? productUrlFor(opener) : add && cardAddUrl(add.form, add) };
  };
  const settle = (unit, answer) => {
    if (answer === 'required') {
      markScope(unit);
      unitTarget(unit).add?.setAttribute('data-cq-bold-checked', '');
    }
    unit.removeAttribute(HOLD);
  };
  // Takes a held set over (no time limit), unless the rendered 15 s hold has already run out and the shopper is on it: a
  // focused or pointed-at button is never hidden from under them.
  const rehold = (unit, state) => {
    if (!unit.hasAttribute(HOLD) || unit.getAttribute(HOLD) === 'soft') return;
    const onIt = unit.contains(document.activeElement) || (unit.closest('.cq-card') || unit).matches(':hover');
    if (onIt && unit.getAttribute(HOLD) === '' && getComputedStyle(unit).visibility === 'visible') return;
    unit.setAttribute(HOLD, state);
  };
  const MAX_CHECKS = 6;
  // Lower runs first: 0 held and on screen (or a unit that is checked as soon as it appears), 1 held and near the
  // screen, 2 not held.
  const queue = [];
  const enqueue = (unit, onScreen = true) => {
    queue.push({ unit, rank: unit.hasAttribute(HOLD) ? (onScreen ? 0 : 1) : 2 });
  };
  let running = 0;
  const pump = () => {
    while (running < MAX_CHECKS && queue.length) {
      let pick = 0;
      for (let i = 1; i < queue.length; i += 1) if (queue[i].rank < queue[pick].rank) pick = i;
      const { unit } = queue.splice(pick, 1)[0];
      const { subject, url } = unitTarget(unit);
      if (!unit.isConnected || !subject || !url) { unit.removeAttribute(HOLD); continue; }
      rehold(unit, 'check');
      const timer = unit.hasAttribute(HOLD) ? setTimeout(() => unit.removeAttribute(HOLD), BOLD_WAIT) : 0;
      running += 1;
      boldRequired(subject, url).then((answer) => { clearTimeout(timer); settle(unit, answer); })
        .finally(() => { running -= 1; pump(); });
    }
  };
  // A hidden element (display: none, such as the compact rows while the grid shows) never intersects, so it is taken
  // only once it shows.
  const viewer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      viewer.unobserve(entry.target);
      const box = entry.boundingClientRect;
      enqueue(entry.target, box.bottom > 0 && box.top < window.innerHeight);
    });
    pump();
  }, { rootMargin: '300px 0px' }) : null;
  const seen = new WeakSet();
  const track = (unit, late) => {
    if (seen.has(unit)) return;
    seen.add(unit);
    // A held set's own buttons are settled with it.
    unit.querySelectorAll(UNITS).forEach((inner) => seen.add(inner));
    const { subject, url } = unitTarget(unit);
    const key = subject && url && boldKey(subject, url);
    if (!key) { unit.removeAttribute(HOLD); return; }
    if (boldAnswers.has(key)) { settle(unit, boldAnswers.get(key)); return; }
    // Product cards and collection rows (the grid and the compact rows) wait until they come near the screen (a grid
    // holds dozens); the few others (a compare column, cart suggestions, a search result) are checked at once.
    if (viewer && unit.closest('.cq-card, #product-grid, #product-rows')) { rehold(unit, 'wait'); viewer.observe(unit); return; }
    if (late && !unit.hasAttribute(HOLD) && unit.matches('[type="submit"]')) unit.setAttribute(HOLD, 'soft');
    enqueue(unit);
    pump();
  };
  const scan = (root, late = false) => {
    if (root.matches?.(UNITS)) track(root, late);
    root.querySelectorAll(UNITS).forEach((unit) => track(unit, late));
  };
  const start = () => {
    scan(document);
    // Units added later (recommendations, recently viewed, filtered grids, the compare dialog, cart and search renders)
    // are taken as they arrive; a mutation callback runs before the next paint.
    new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => { if (node.nodeType === 1) scan(node, true); }));
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();
